import React, { useLayoutEffect, useRef } from 'react';
import { formatearTiempo } from '../utils/quizUtils';
import buildImageUrl from '../config/imageUrlHelper';
import { FaUserCircle } from 'react-icons/fa';
import { derivarAutorPregunta } from '../utils/quiz/autorPregunta';

const MAX_QUESTION_FONT_SIZE = 28;
const MIN_QUESTION_FONT_SIZE = 10;
const MAX_QUESTION_FONT_SIZE_MOBILE = 22;
const MIN_QUESTION_FONT_SIZE_MOBILE = 12;

const ajustarFuentePregunta = (container, textElement) => {
  if (!container || !textElement) {
    return;
  }

  const esMobile = window.innerWidth < 640;
  const tamañoMaximo = esMobile ? MAX_QUESTION_FONT_SIZE_MOBILE : MAX_QUESTION_FONT_SIZE;
  const tamañoMinimo = esMobile ? MIN_QUESTION_FONT_SIZE_MOBILE : MIN_QUESTION_FONT_SIZE;
  let tamañoActual = tamañoMaximo;
  textElement.style.fontSize = `${tamañoActual}px`;

  if (esMobile) {
    const obtenerLimiteDosLineas = () => {
      const estilo = window.getComputedStyle(textElement);
      const lineHeight = parseFloat(estilo.lineHeight);
      if (Number.isFinite(lineHeight) && lineHeight > 0) {
        return lineHeight * 2;
      }
      return tamañoActual * 1.2 * 2;
    };

    let limiteDosLineas = obtenerLimiteDosLineas();
    while (textElement.scrollHeight > limiteDosLineas && tamañoActual > tamañoMinimo) {
      tamañoActual -= 1;
      textElement.style.fontSize = `${tamañoActual}px`;
      limiteDosLineas = obtenerLimiteDosLineas();
    }
  } else {
    while (textElement.scrollWidth > container.clientWidth && tamañoActual > tamañoMinimo) {
      tamañoActual -= 1;
      textElement.style.fontSize = `${tamañoActual}px`;
    }
  }
};

// Componente para mostrar la información del quiz
const QuizDisplay = ({ 
  preguntaActual, 
  preguntas, 
  pregunta, 
  puntuacion, 
  tiempoRestante, 
  duracionConcurso,
  respuestasVisibles,
  respuestasEliminadas,
  respuestas,
  respuestasMezcladasPreguntaActual,
  respuestasMezcladas, // Añadir la prop para el array completo de respuestas mezcladas
  indiceRespuestaCorrecta,
  mostrarDialogoConfirmacion,
  setMostrarDialogoConfirmacion,
  resolverPregunta,
  siguientePregunta,
  reiniciarConcurso,
  respuestaSeleccionada,
  respuestaRevelada,
  manejarSeleccionRespuesta,
  setRespuestaSeleccionada
}) => {
  // Calcular el porcentaje de progreso
  const porcentajeProgreso = preguntas.length > 1 ? (preguntaActual / (preguntas.length - 1)) * 100 : 0;
  const {
    nombreAutor,
    fotoAutor,
    mostrarFoto: mostrarFotoAutor,
    mostrarIcono: mostrarIconoAutor,
  } = derivarAutorPregunta(pregunta);
  const contenedorPreguntaRef = useRef(null);
  const textoPreguntaRef = useRef(null);

  useLayoutEffect(() => {
    const actualizarFuente = () => {
      ajustarFuentePregunta(contenedorPreguntaRef.current, textoPreguntaRef.current);
    };

    actualizarFuente();
    window.addEventListener('resize', actualizarFuente);

    return () => {
      window.removeEventListener('resize', actualizarFuente);
    };
  }, [pregunta?.pregunta]);

  return (
    <div className="lg:w-2/3 bg-white rounded-lg shadow-md p-4 dark:bg-gray-800 dark:text-white transition-colors duration-300 flex flex-col min-w-0">
      {/* Barra de progreso con círculos superpuestos */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex flex-col md:flex-row justify-between items-center mb-2 dark:text-white gap-2 text-sm">
          <span className="font-medium dark:text-white">Pregunta {preguntaActual + 1} de {preguntas.length}</span>
          <span className="font-medium dark:text-white">Puntuación: {puntuacion}</span>
          {duracionConcurso > 0 && (
            <span className={`font-medium ${tiempoRestante <= 30 ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>
              Tiempo: {formatearTiempo(tiempoRestante)}
            </span>
          )}
        </div>
        <div className="relative h-6"> {/* Contenedor para superponer círculos y barra */}
          {/* Barra de progreso */}
          <div className="absolute w-full h-2 top-1/2 left-0 bg-gray-200 rounded-full dark:bg-gray-700 z-0 transform -translate-y-1/2">
            <div 
              className="absolute h-2 rounded-full transition-all duration-300 bg-blue-600 dark:bg-blue-500"
              style={{ width: `${porcentajeProgreso}%` }}
            ></div>
          </div>
          {/* Círculos superpuestos */}
          <div className="absolute top-1/2 left-0 w-full flex justify-between z-10 px-1 transform -translate-y-1/2">
            {preguntas.map((preg, index) => {
              // Calcular el porcentaje exacto de posición para cada círculo
              const porcentajePosicion = (index / (preguntas.length - 1)) * 100;
              
            let circleClass = "w-4 h-4 rounded-full absolute";
            
            if (respuestas[index] !== null) {
              // Ya se respondió esta pregunta
              const respuestaMezclada = respuestasMezcladas[index];
              if (respuestaMezclada && respuestas[index] === respuestaMezclada.findIndex(r => r.esCorrecta)) {
                // La respuesta fue correcta
                circleClass += " bg-green-500";
              } else {
                // La respuesta fue incorrecta
                circleClass += " bg-red-500";
              }
            } else {
              // Aún no se responde esta pregunta
              circleClass += " bg-white border-2 border-gray-300";
            }
              
              return (
                <div 
                  key={index} 
                  className={circleClass}
                  style={{ left: `${porcentajePosicion}%`, transform: 'translateX(-50%) translateY(-50%)' }}
                ></div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Pregunta */}
      <div className="mb-4 flex items-center gap-3 flex-shrink-0">
        <div ref={contenedorPreguntaRef} className="flex-1 min-w-0 flex items-center justify-center text-center">
          <h2
            ref={textoPreguntaRef}
            className="font-bold dark:text-white flex-grow break-words leading-tight overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] sm:[display:block] sm:[-webkit-line-clamp:unset]"
            style={{ fontSize: `${MAX_QUESTION_FONT_SIZE}px` }}
            title={pregunta.pregunta}
          >
            {pregunta.pregunta}
          </h2>
        </div>
        {mostrarFotoAutor && (
          <img
            src={buildImageUrl(fotoAutor)}
            alt={`Foto de ${nombreAutor}`}
            title={nombreAutor}
            className="w-10 h-10 rounded-full object-cover border border-slate-300 dark:border-slate-600 shrink-0"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        )}
        {mostrarIconoAutor && (
          <span
            title={nombreAutor}
            aria-label={`Autor: ${nombreAutor}`}
            className="shrink-0 text-slate-500 dark:text-slate-300"
          >
            <FaUserCircle className="w-10 h-10" />
          </span>
        )}
      </div>

      {/* Área de respuestas - Clic para revelar progresivamente */}
      <div className="mb-4 flex-grow flex flex-col justify-center">
        {/* Mostrar las respuestas */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {respuestasMezcladasPreguntaActual.map((respuesta, index) => {
            // Verificar si esta respuesta específica está eliminada por el comodín 50/50
            const estaEliminada = respuestasEliminadas[preguntaActual]?.includes(index) || false;
            // La visibilidad debe considerar tanto el número de respuestas que se pueden revelar gradualmente
            // como las respuestas eliminadas por el comodín 50/50
            // Las respuestas solo deben estar ocultas al principio de cada proceso de pregunta (cuando index >= respuestasVisibles)
            // pero no deben ocultarse adicionalmente por el comodín 50/50
            const estaVisible = (index < respuestasVisibles) && !estaEliminada;
            let buttonClass = estaVisible ? "p-3 border-2 rounded-lg text-left break-words min-w-0 hover:bg-gray-50 transition-colors dark:hover:bg-gray-600 respuesta-visible min-h-[60px] flex items-center" : "p-3 border-2 rounded-lg text-left break-words min-w-0 opacity-70 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 cursor-not-allowed text-sm min-h-[60px] flex items-center";
            
            if (estaVisible) {
              if (respuestas[preguntaActual] !== null) {
                // Ya se ha respondido definitivamente a esta pregunta
                if (index === indiceRespuestaCorrecta) { // Respuesta correcta
                  buttonClass += " bg-green-500 text-white border-green-500 dark:bg-green-600";
                } else if (index === respuestas[preguntaActual]) { // Respuesta seleccionada incorrecta
                  buttonClass += " bg-red-500 text-white border-red-500 dark:bg-red-600";
                } else {
                  buttonClass += " border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white";
                }
              } else if (respuestaRevelada) {
                // Se ha revelado la respuesta pero aún no se ha marcado definitivamente
                if (index === indiceRespuestaCorrecta) { // Respuesta correcta
                  buttonClass += " bg-green-500 text-white border-green-500 dark:bg-green-600";
                } else if (index === respuestaSeleccionada) { // Respuesta seleccionada incorrecta
                  buttonClass += " bg-red-500 text-white border-red-500 dark:bg-red-600";
                } else {
                  buttonClass += " border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white";
                }
              } else if (respuestaSeleccionada === index) {
                // Esta respuesta está seleccionada temporalmente
                buttonClass += " bg-yellow-200 text-black border-yellow-200 dark:bg-yellow-600 dark:text-white dark:border-yellow-600";
              } else {
                buttonClass += " border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white";
              }
            } else if (estaEliminada) {
              // Añadir estilos específicos para respuestas eliminadas con el comodín 50/50
              buttonClass = "p-3 border-2 rounded-lg text-left break-words min-w-0 opacity-40 bg-gray-300 dark:bg-gray-700 border-gray-300 dark:border-gray-600 cursor-not-allowed text-sm min-h-[60px] flex items-center";
            }

            // Identificadores fijos para las respuestas
            const identificadores = ['a)', 'b)', 'c)', 'd)'];
            
            return (
              <button
                key={index}
                className={buttonClass}
                onClick={(e) => {
                  e.stopPropagation(); // Evitar que el clic en el botón active la revelación de respuesta
                  if (estaVisible) {
                    // Si la respuesta ya está seleccionada y aún no se ha revelado, deseleccionarla
                    if (respuestaSeleccionada === index && !respuestaRevelada && respuestas[preguntaActual] === null) {
                      setRespuestaSeleccionada(null);
                    } else {
                      manejarSeleccionRespuesta(index);
                    }
                  }
                }}
                disabled={respuestas[preguntaActual] !== null || (respuestaRevelada && index !== respuestaSeleccionada) || !estaVisible || estaEliminada}
                data-es-correcta={respuesta.esCorrecta}
              >
                <span className="font-bold mr-2">{identificadores[index]} </span>
                {estaVisible ? respuesta.texto : estaEliminada ? <span className="text-xs text-gray-400 dark:text-gray-500 italic">Respuesta eliminada</span> : <span className="text-xs text-gray-400 dark:text-gray-500 italic">Respuesta oculta</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Botones de control */}
      <div className="flex flex-wrap justify-center gap-3 flex-shrink-0">
        <button
          onClick={reiniciarConcurso}
          className="bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700 transition-colors dark:bg-gray-700 dark:hover:bg-gray-800 duration-300 text-sm"
        >
          Reiniciar
        </button>
        {!respuestaRevelada ? (
          <button
            onClick={() => resolverPregunta()}
            disabled={respuestaSeleccionada === null}
            className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed dark:bg-blue-700 dark:hover:bg-blue-800 duration-300 text-sm"
          >
            Resolver
          </button>
        ) : null}
        {respuestaRevelada && preguntaActual < preguntas.length - 1 && (
          <button
            onClick={siguientePregunta}
            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition-colors dark:bg-green-700 dark:hover:bg-green-800 duration-300 text-sm"
          >
            Siguiente
          </button>
        )}
        <button
          onClick={() => setMostrarDialogoConfirmacion(true)}
          className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition-colors dark:bg-red-700 dark:hover:bg-red-800 duration-300 text-sm"
        >
          {preguntaActual === preguntas.length - 1 ? 'Terminar Concurso' : 'Finalizar'}
        </button>
      </div>
    </div>
  );
};

export default QuizDisplay;
